import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Input, FormField } from '../../../../components/ui/FormField';
import { FormSelect } from '../../../../components/ui/FormSelect';
import { useToast } from '../../../../components/ui/Toast';
import { useCreateFeeSlab } from '../../../../features/settings/network/fee-slabs.api';
import { z } from 'zod';
import { Banner } from '../../../../components/ui/Banner';

export const Route = createFileRoute('/_auth/settings/fee-slabs/new')({
  component: CreateFeeSlabPage,
});

const feeSlabSchema = z.object({
  feeType: z.string().min(1, 'Fee type is required'),
  calculationType: z.enum(['flat_amount', 'fixed', 'percentage', 'slabs', 'slabbed', 'waived']),
  configJson: z.string().refine((val) => {
    try {
      JSON.parse(val);
      return true;
      // allow-silent-catch: JSON.parse errors are expected during validation
    } catch {
      return false;
    }
  }, 'Configuration must be valid JSON'),
  effectiveFrom: z.string().min(1, 'Effective date is required'),
});

type FeeSlabForm = z.infer<typeof feeSlabSchema>;

const feeSlabFormConfig = defineFormPage<FeeSlabForm>({
  resource: 'fee-slabs',
  defaultValues: {
    feeType: '',
    calculationType: 'flat_amount',
    configJson: '{\n  "amountPaise": 50000\n}',
    effectiveFrom: '',
  },
  schema: feeSlabSchema,
});

function CreateFeeSlabPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createFeeSlab = useCreateFeeSlab();

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(feeSlabFormConfig, createFeeSlab as { mutateAsync: (values: unknown) => Promise<unknown> }, {
    routeId: 'settings/fee-slabs/new',
    onBeforeSubmit: (value) => {
      const config = JSON.parse(value.configJson);
      return {
        feeType: value.feeType,
        calculationType: value.calculationType,
        config,
        effectiveFrom: value.effectiveFrom,
      };
    },
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Submitted for Approval',
        description: 'Fee slab creation request has been submitted for approval.',
      });
      void navigate({ to: '/settings/fee-slabs' as const });
    },
    onError: (err: { message?: string }) => {
      toast({
        type: 'error',
        title: 'Error',
        description: err.message || 'Failed to create fee slab. Please try again.',
      });
    },
  });

  return (
    <FormPageLayout
      title="Add Fee Slab"
      subtitle="Define a new pricing, penalty, or service fee calculation slab."
      backTo="/settings/fee-slabs"
      variant="card"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <form.Field
              name="feeType"
              validators={{ onChange: ({ value }: { value: unknown }) => feeSlabSchema.shape.feeType.safeParse(value).success ? undefined : feeSlabSchema.shape.feeType.safeParse(value).error?.issues[0]?.message }}
              children={(field: { state: { value: string; meta: unknown }; name: string; handleBlur: () => void; handleChange: (val: string) => void }) => (
                <FormField label="Fee Type" required>
                  <Input
                    placeholder="e.g. late_payment, bounce_charge"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <form.Field
              name="calculationType"
              children={(field: { state: { value: string; meta: unknown }; name: string; handleBlur: () => void; handleChange: (val: string) => void }) => (
                <FormSelect
                  label="Calculation Type"
                  options={[
                    { value: 'flat_amount', label: 'Flat Amount (fixed)' },
                    { value: 'percentage', label: 'Percentage-based' },
                    { value: 'slabs', label: 'Slabbed Ranges' },
                    { value: 'waived', label: 'Waived' },
                  ]}
                  value={field.state.value}
                  onChange={e => field.handleChange(e.target.value as 'flat_amount' | 'fixed' | 'percentage' | 'slabs' | 'slabbed' | 'waived')}
                  disabled={isSubmitting}
                />
              )}
            />

            <form.Field
              name="configJson"
              validators={{ onChange: ({ value }: { value: unknown }) => feeSlabSchema.shape.configJson.safeParse(value).success ? undefined : feeSlabSchema.shape.configJson.safeParse(value).error?.issues[0]?.message }}
              children={(field: { state: { value: string; meta: unknown }; name: string; handleBlur: () => void; handleChange: (val: string) => void }) => (
                <FormField label="Configuration JSON" required>
                  <textarea
                    id={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                    className="w-full min-h-[120px] p-3 font-mono bg-surface-3 border border-border rounded-[6px] text-xs text-text-primary focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500 disabled:opacity-50"
                  />
                </FormField>
              )}
            />

            <form.Field
              name="effectiveFrom"
              validators={{ onChange: ({ value }: { value: unknown }) => feeSlabSchema.shape.effectiveFrom.safeParse(value).success ? undefined : feeSlabSchema.shape.effectiveFrom.safeParse(value).error?.issues[0]?.message }}
              children={(field: { state: { value: string; meta: unknown }; name: string; handleBlur: () => void; handleChange: (val: string) => void }) => (
                <FormField label="Effective From" required>
                  <Input
                    type="date"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <FormActions onCancel={() => void navigate({ to: '/settings/fee-slabs' as const })} isSubmitting={isSubmitting} submitLabel="Save Slab" />
          </form>
    </FormPageLayout>
  );
}
